import type * as api from "./api";

/** Design 26.11 F4: the text of the source tag of an account. An older API sends no tag. */
export function sourceTagText(tag: api.DataSource | undefined): string {
  if (!tag) return "";
  if (tag.state === "scheduled") return `${tag.label} → app on ${tag.from ?? "a later date"}`;
  return tag.label;
}

/** The hover help of each source tag (rule U-28, design 26.11 F4). */
export const SOURCE_HELP: Record<api.DataSourceState, string> = {
  sheet: "The source owns the fields of this account. The next import writes them, and a change in the app does not stay.",
  scheduled: "The source owns the fields until the switch date of the area. From that date the app owns them, and the import stops writing them.",
  app: "The app owns the fields of this account. The import does not change them.",
};

/** The help of a source tag, with the date of a scheduled switch. */
export function sourceHelp(tag: api.DataSource | undefined): string {
  if (!tag) return "";
  if (tag.state === "scheduled" && tag.from) return `The source owns the fields until ${tag.from}, the switch date of the area. From that date the app owns them, and the import stops writing them.`;
  return SOURCE_HELP[tag.state];
}

export type SourceFilter = "" | api.DataSourceState;

export const SOURCE_FILTERS: { value: SourceFilter; label: string }[] = [
  { value: "", label: "Any source" },
  { value: "sheet", label: "Sheet" },
  { value: "scheduled", label: "Sheet → app" },
  { value: "app", label: "App" },
];

/** An account matches when no filter is set, or when its tag has the state. An untagged row matches only no filter. */
export function sourceMatches(tag: api.DataSource | undefined, filter: SourceFilter): boolean {
  return filter === "" || tag?.state === filter;
}

/** The sort key of the source column. */
export const sourceKey = (tag: api.DataSource | undefined): string => (tag ? `${tag.state}:${tag.from ?? ""}` : "");

/** The labels of the fields that freeze (rule T-9). */
export const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  address: "Address",
  status: "Status",
  commercial_status: "Contract status",
  area: "Area",
  week_group: "Week group",
  window: "Window",
  service_minutes: "Service time",
  stated_rate: "Gallons a week",
  capacity: "Container size",
  door: "Front door pin",
  tank: "Container pin",
};

export const fieldsText = (fields: readonly string[] | undefined): string =>
  (fields ?? []).map((field) => FIELD_LABELS[field] ?? field).join(", ");

/** The text of the counts of a map, such as the open issues by code. */
export function countsText(counts: Record<string, number> | undefined, labels: Record<string, string> = {}): string {
  const listed = Object.entries(counts ?? {}).filter(([, n]) => n > 0);
  return listed.length ? listed.map(([key, n]) => `${labels[key] ?? key}: ${n}`).join(", ") : "None";
}
